import React, { ReactNode } from 'react';
import { Image, StyleSheet } from 'react-native';

import ShieldIcon from '@/assets/icons/shield';
import { EventCardPlayerIcon } from '@/feature/lobby/components/EventCardPlayerIcon';

import { SizedBox } from '../SizedBox';
import { StyleVariant } from '../TextComponent';
import { Text } from '../TextComponent';
import { Box, Row } from '../lib/components';
import { ds } from '../lib/design.system';
import { AppSpacing } from '../lib/theme';
import { TeamInfo } from './types';

export type MatchupColumnTheme = {
    gameInfoHeight: number;
    gameInfoVariant: StyleVariant;
    gameInfoStatusVariant: StyleVariant;
    teamNameVariant: StyleVariant;
    innerPadding: AppSpacing;
    logoWidth: number;
    logoHeight: number;
    sizedBoxDimensions: { top: number; middle: number };
};

export type MatchupColumnProps = {
    team1: TeamInfo;
    team2: TeamInfo;
    team1Indicator?: ReactNode;
    team2Indicator?: ReactNode;
    theme: MatchupColumnTheme;
    matchDetails?: string;
    periodDetails?: string;
    maxWidth: number;
    isLive?: boolean;
};

type TeamRowProps = {
    team: TeamInfo;
    indicator?: ReactNode;
    logoWidth: number;
    logoHeight: number;
    teamNameVariant: StyleVariant;
};

const TeamRow = ({ team, indicator, logoWidth, logoHeight, teamNameVariant }: TeamRowProps) => {
    const image =
        team.logoVariant === 'player' ? (
            <EventCardPlayerIcon
                uri={team.icon}
                size={{ width: logoWidth, height: logoHeight }}
                placeholderSize={{ width: 24, height: 21 }}
                backgroundColor={ds.colorToken.background.neutral.subtle}
                borderRadius="r8"
            />
        ) : team.shouldShowFallbackLogo && team.colors?.length === 2 ? (
            <ShieldIcon primary={team.colors[0]} secondary={team.colors[1]} height={logoHeight} width={logoWidth} />
        ) : team.icon ? (
            <Image source={{ uri: team.icon }} style={{ height: logoHeight, width: logoWidth }} />
        ) : null;

    return (
        <Row alignItems="center">
            {image ? <Box mr="s8">{image}</Box> : null}
            <Text variant={teamNameVariant} numberOfLines={1} style={styles.teamName}>
                {team.shortCode}
            </Text>
            {indicator ? <Box ml="s6">{indicator}</Box> : null}
        </Row>
    );
};

export const MatchupColumn = ({
    team1,
    team2,
    team1Indicator,
    team2Indicator,
    theme,
    matchDetails,
    periodDetails,
    maxWidth,
    isLive = false,
}: MatchupColumnProps) => {
    const {
        gameInfoHeight,
        gameInfoVariant,
        gameInfoStatusVariant,
        teamNameVariant,
        innerPadding,
        logoWidth,
        logoHeight,
        sizedBoxDimensions,
    } = theme;

    const teamRowProps = {
        logoWidth,
        logoHeight,
        teamNameVariant,
    };

    return (
        <Box paddingRight={innerPadding} maxWidth={maxWidth}>
            <Row width="100%" height={gameInfoHeight} alignItems="center" columnGap="s3">
                {matchDetails ? (
                    <Text
                        variant={gameInfoStatusVariant}
                        color={isLive ? ds.colorToken.text.danger : 'neutral300'}
                        numberOfLines={1}
                    >
                        {matchDetails}
                    </Text>
                ) : null}
                {periodDetails ? (
                    <>
                        <Text variant={gameInfoVariant} color="neutral300">
                            {'•'}
                        </Text>
                        <Text variant={gameInfoVariant} color="neutral300" numberOfLines={1}>
                            {periodDetails}
                        </Text>
                    </>
                ) : null}
            </Row>
            <SizedBox value={sizedBoxDimensions.top} />
            <TeamRow team={team1} indicator={team1Indicator} {...teamRowProps} />
            <SizedBox value={sizedBoxDimensions.middle} />
            <TeamRow team={team2} indicator={team2Indicator} {...teamRowProps} />
        </Box>
    );
};

const styles = StyleSheet.create({
    teamName: { flexShrink: 1 },
});
